import type { RevenuePeriod } from "@cleanbudget/contract/revenue";
import type { WorkQueue } from "@cleanbudget/contract/work-queue";
import { Badge } from "@cleanbudget/ui/components/badge";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { IconArrowRight, IconCheck } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, type LinkProps } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { HealthCard, healthQuery } from "@/components/health-card";
import { HelpButton } from "@/components/help/help-button";
import type { HelpTopicId } from "@/components/help/topics";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { resolveLabel } from "@/lib/attention";
import { formatCalendarDate, formatDateTime } from "@/lib/format";
import { formatCents } from "@/lib/money";
import { orpc } from "@/lib/orpc";

const workQueueQuery = () => orpc.workQueue.get.queryOptions();
const revenueSummaryQuery = () => orpc.revenue.summary.queryOptions();

export const Route = createFileRoute("/_admin/")({
	// The pattern for every route: the loader fills the query cache, the component reads it with
	// useSuspenseQuery, so the page renders with its data or not at all.
	loader: ({ context }) =>
		Promise.all([
			context.queryClient.ensureQueryData(workQueueQuery()),
			context.queryClient.ensureQueryData(revenueSummaryQuery()),
			context.queryClient.ensureQueryData(healthQuery()),
		]),
	component: StartPage,
});

/**
 * Start (spec, story 6 and "Startpagina"): the revenue of today, this week and this month, and
 * the work queue: per kind how many and the first few, each linking to where it is handled.
 */
function StartPage() {
	const { t } = useTranslation("start");
	const { session } = Route.useRouteContext();
	const { data: queue } = useSuspenseQuery(workQueueQuery());
	const nothingToDo =
		queue.attention.total +
			queue.toShip.total +
			queue.applications.total +
			queue.overdueInvoices.total +
			queue.outOfStock.total +
			queue.lowStock.total +
			queue.backgroundFailures.total +
			queue.reviews.total ===
		0;
	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("title")}
				description={t("welcome", { name: session.employee.name })}
				help="start:start"
			/>
			<RevenueSummary />
			<section className="flex flex-col gap-3" aria-labelledby="work-queue">
				<div className="flex items-center gap-1.5">
					<h2 id="work-queue" className="font-heading text-lg font-semibold">
						{t("queue.title")}
					</h2>
					<HelpButton topic="start:queue" />
				</div>
				{nothingToDo ? <p className="text-muted-foreground">{t("queue.nothing")}</p> : null}
				<div className="grid items-start gap-4 lg:grid-cols-2">
					<AttentionCard queue={queue} />
					<ToShipCard queue={queue} />
					<OverdueCard queue={queue} />
					<ApplicationsCard queue={queue} />
					<ReviewsCard queue={queue} />
					<StockCard queue={queue} />
					<BackgroundFailuresCard queue={queue} />
				</div>
			</section>
			<HealthCard />
		</div>
	);
}

function RevenueSummary() {
	const { t } = useTranslation("start");
	const { data } = useSuspenseQuery(revenueSummaryQuery());
	const periods: readonly { key: "today" | "week" | "month"; period: RevenuePeriod }[] = [
		{ key: "today", period: data.today },
		{ key: "week", period: data.week },
		{ key: "month", period: data.month },
	];
	return (
		<section className="flex flex-col gap-3" aria-labelledby="revenue-summary">
			<div className="flex items-baseline justify-between gap-3">
				<div className="flex items-center gap-1.5">
					<h2 id="revenue-summary" className="font-heading text-lg font-semibold">
						{t("revenue.title")}
					</h2>
					<HelpButton topic="start:revenue" />
				</div>
				<Link
					to="/revenue"
					className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
				>
					{t("revenue.more")}
					<IconArrowRight className="size-4" aria-hidden />
				</Link>
			</div>
			<dl className="grid gap-3 sm:grid-cols-3" data-testid="revenue-summary">
				{periods.map(({ key, period }) => (
					<Link
						key={key}
						to="/revenue"
						search={{ preset: key }}
						className="flex flex-col gap-1 rounded-2xl p-4 ring-1 ring-foreground/10 transition-colors hover:bg-muted/50"
					>
						<dt className="text-sm text-muted-foreground">{t(`revenue.${key}`)}</dt>
						<dd className="font-heading text-2xl font-semibold tabular-nums">
							{formatCents(period.totals.total)}
						</dd>
						<dd className="text-xs text-muted-foreground tabular-nums">
							{t("revenue.split", {
								consumer: formatCents(period.totals.consumer.goods + period.totals.consumer.shipping),
								business: formatCents(period.totals.business.goods + period.totals.business.shipping),
							})}
						</dd>
					</Link>
				))}
			</dl>
			<p className="text-xs text-muted-foreground">{t("revenue.exclVat")}</p>
		</section>
	);
}

/** One kind of work: its title and count, the first few items, and a link to all of them. */
function QueueCard({
	title,
	total,
	all,
	empty,
	children,
	testId,
	help,
}: {
	title: string;
	total: number;
	all?: { label: string; link: LinkProps } | undefined;
	empty: string;
	children: ReactNode;
	testId: string;
	help: HelpTopicId;
}) {
	return (
		<Card size="sm" data-testid={testId}>
			<CardHeader>
				<CardTitle className="flex items-center gap-1.5">
					<h3>{title}</h3>
					<HelpButton topic={help} />
				</CardTitle>
				<CardAction>
					<Badge variant={total > 0 ? "default" : "secondary"} className="tabular-nums">
						{total}
					</Badge>
				</CardAction>
			</CardHeader>
			<CardContent className="flex flex-col gap-3">
				{total === 0 ? (
					<p className="text-muted-foreground">{empty}</p>
				) : (
					<ul className="flex flex-col divide-y divide-border">{children}</ul>
				)}
				{all && total > 0 ? (
					<Link {...all.link} className="flex w-fit items-center gap-1 text-sm font-medium hover:underline">
						{all.label}
						<IconArrowRight className="size-4" aria-hidden />
					</Link>
				) : null}
			</CardContent>
		</Card>
	);
}

/** A row: what it is (a link to where it is handled) and a detail line, with something on the right. */
function QueueItem({ title, detail, aside }: { title: ReactNode; detail?: ReactNode; aside?: ReactNode }) {
	return (
		<li className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0">
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="min-w-0 break-words">{title}</span>
				{detail ? <span className="text-xs break-words text-muted-foreground">{detail}</span> : null}
			</div>
			{aside ? <div className="shrink-0 text-right">{aside}</div> : null}
		</li>
	);
}

const linkClass = "font-medium hover:underline";

function AttentionCard({ queue }: { queue: WorkQueue }) {
	const { t } = useTranslation("start");
	const { t: tOrder } = useTranslation("order");
	const queryClient = useQueryClient();
	const resolve = useMutation({
		...orpc.order.resolveAttention.mutationOptions(),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: orpc.workQueue.get.key() }),
				queryClient.invalidateQueries({ queryKey: orpc.order.get.key() }),
			]),
	});
	return (
		<QueueCard
			testId="queue-attention"
			help="start:attention"
			title={t("queue.attention.title")}
			total={queue.attention.total}
			empty={t("queue.attention.empty")}
		>
			{queue.attention.items.map((item) => (
				<QueueItem
					key={item.eventId}
					title={
						<span className="flex flex-wrap items-center gap-2">
							<Link to="/order/$orderId" params={{ orderId: item.orderId }} className={linkClass}>
								{item.orderNumber}
							</Link>
							<StatusBadge tone="attention">{tOrder(`attention.${item.attention}`)}</StatusBadge>
						</span>
					}
					detail={[item.name, item.message, formatDateTime(item.at)].filter(Boolean).join(" · ")}
					aside={
						<Button
							size="sm"
							variant="outline"
							disabled={resolve.isPending}
							onClick={() => resolve.mutate({ orderId: item.orderId, eventId: item.eventId })}
						>
							<IconCheck aria-hidden />
							{tOrder(resolveLabel(item.attention))}
						</Button>
					}
				/>
			))}
			{resolve.isError ? (
				<li className="pt-2 text-xs text-destructive">{tOrder("attention.failed")}</li>
			) : null}
		</QueueCard>
	);
}

function ToShipCard({ queue }: { queue: WorkQueue }) {
	const { t } = useTranslation("start");
	return (
		<QueueCard
			testId="queue-to-ship"
			help="start:toShip"
			title={t("queue.toShip.title")}
			total={queue.toShip.total}
			empty={t("queue.toShip.empty")}
			all={{ label: t("queue.toShip.all"), link: { to: "/order", search: { toShip: "yes" } } }}
		>
			{queue.toShip.items.map((item) => (
				<QueueItem
					key={item.orderId}
					title={
						<Link to="/order/$orderId" params={{ orderId: item.orderId }} className={linkClass}>
							{item.orderNumber}
						</Link>
					}
					detail={[item.company ?? item.name, formatDateTime(item.placedAt)].join(" · ")}
					aside={<span className="tabular-nums">{formatCents(item.totalIncl)}</span>}
				/>
			))}
		</QueueCard>
	);
}

function OverdueCard({ queue }: { queue: WorkQueue }) {
	const { t } = useTranslation("start");
	const overdue = queue.overdueInvoices;
	return (
		<QueueCard
			testId="queue-overdue"
			help="start:overdue"
			title={t("queue.overdue.title")}
			total={overdue.total}
			empty={t("queue.overdue.empty")}
			all={{
				label: t("queue.overdue.all", { amount: formatCents(overdue.totalAmount) }),
				link: { to: "/order/invoices", search: { state: "overdue" } },
			}}
		>
			{overdue.items.map((item) => (
				<QueueItem
					key={item.invoiceId}
					title={
						<Link to="/order/$orderId" params={{ orderId: item.orderId }} className={linkClass}>
							{item.number}
						</Link>
					}
					detail={[
						item.customerName,
						t("queue.overdue.days", { count: item.daysOverdue, date: formatCalendarDate(item.dueOn) }),
						item.reminders > 0 ? t("queue.overdue.reminders", { count: item.reminders }) : undefined,
					]
						.filter(Boolean)
						.join(" · ")}
					aside={<span className="tabular-nums">{formatCents(item.totalIncl)}</span>}
				/>
			))}
		</QueueCard>
	);
}

function ApplicationsCard({ queue }: { queue: WorkQueue }) {
	const { t } = useTranslation("start");
	return (
		<QueueCard
			testId="queue-applications"
			help="start:applications"
			title={t("queue.applications.title")}
			total={queue.applications.total}
			empty={t("queue.applications.empty")}
			all={{
				label: t("queue.applications.all"),
				link: { to: "/customer", search: { businessStatus: "pending" } },
			}}
		>
			{queue.applications.items.map((item) => (
				<QueueItem
					key={item.customerId}
					title={
						<Link to="/customer/$customerId" params={{ customerId: item.customerId }} className={linkClass}>
							{item.companyName ?? item.name ?? item.email}
						</Link>
					}
					detail={[item.name, item.email].filter(Boolean).join(" · ")}
					aside={<span className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</span>}
				/>
			))}
		</QueueCard>
	);
}

/** "Reviews te beoordelen" (#260): the oldest pending first, each linking to where it is judged. */
function ReviewsCard({ queue }: { queue: WorkQueue }) {
	const { t } = useTranslation("start");
	const { t: tReview } = useTranslation("productReview");
	return (
		<QueueCard
			testId="queue-reviews"
			help="start:reviews"
			title={t("queue.reviews.title")}
			total={queue.reviews.total}
			empty={t("queue.reviews.empty")}
			all={{
				label: t("queue.reviews.all"),
				link: { to: "/product-review", search: { status: "pending", sort: "writtenAt", order: "asc" } },
			}}
		>
			{queue.reviews.items.map((item) => (
				<QueueItem
					key={item.reviewId}
					title={
						<Link to="/product-review/$reviewId" params={{ reviewId: item.reviewId }} className={linkClass}>
							{item.title ?? item.productName}
						</Link>
					}
					detail={[
						tReview("stars", { count: item.stars }),
						item.title === null ? undefined : item.productName,
						item.firstName ?? tReview("anonymousBuyer"),
					]
						.filter(Boolean)
						.join(" · ")}
					aside={<span className="text-xs text-muted-foreground">{formatDateTime(item.writtenAt)}</span>}
				/>
			))}
		</QueueCard>
	);
}

function StockCard({ queue }: { queue: WorkQueue }) {
	const { t } = useTranslation("start");
	const { outOfStock, lowStock } = queue;
	const items = [
		...outOfStock.items.map((item) => ({ ...item, level: "out" as const })),
		...lowStock.items.map((item) => ({ ...item, level: "low" as const })),
	].slice(0, 5);
	return (
		<QueueCard
			testId="queue-stock"
			help="start:stock"
			title={t("queue.stock.title")}
			total={outOfStock.total + lowStock.total}
			empty={t("queue.stock.empty")}
		>
			{items.map((item) => (
				<QueueItem
					key={item.variantId}
					title={
						<Link to="/stock/$variantId" params={{ variantId: item.variantId }} className={linkClass}>
							{item.variantName === item.productName
								? item.productName
								: `${item.productName} – ${item.variantName}`}
						</Link>
					}
					detail={item.sku}
					aside={
						<StatusBadge tone={item.level === "out" ? "negative" : "attention"}>
							{item.level === "out"
								? t("queue.stock.out")
								: t("queue.stock.low", { quantity: item.quantity })}
						</StatusBadge>
					}
				/>
			))}
			<li className="flex flex-wrap gap-x-4 gap-y-1 pt-2 text-sm">
				{outOfStock.total > 0 ? (
					<Link to="/stock" search={{ stock: "out", activeOnly: "yes" }} className={linkClass}>
						{t("queue.stock.allOut", { count: outOfStock.total })}
					</Link>
				) : null}
				{lowStock.total > 0 ? (
					<Link to="/stock" search={{ stock: "low", activeOnly: "yes" }} className={linkClass}>
						{t("queue.stock.allLow", { count: lowStock.total })}
					</Link>
				) : null}
			</li>
		</QueueCard>
	);
}

/**
 * Background work that failed (registering, a reset, a new confirmation link): which, for which
 * address (masked), and "Afgehandeld" to take it off the queue.
 */
function BackgroundFailuresCard({ queue }: { queue: WorkQueue }) {
	const { t } = useTranslation("start");
	const queryClient = useQueryClient();
	const resolve = useMutation({
		...orpc.backgroundFailure.resolve.mutationOptions(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.workQueue.get.key() }),
	});
	const failures = queue.backgroundFailures;
	return (
		<QueueCard
			testId="queue-background-failures"
			help="start:backgroundFailures"
			title={t("queue.backgroundFailures.title")}
			total={failures.total}
			empty={t("queue.backgroundFailures.empty")}
		>
			{failures.items.map((item) => (
				<QueueItem
					key={item.id}
					title={`${t(`queue.backgroundFailures.work.${item.work}`)}: ${t(`queue.backgroundFailures.failure.${item.failure}`)}`}
					detail={[item.email, formatDateTime(item.at)].join(" · ")}
					aside={
						<Button
							size="sm"
							variant="outline"
							disabled={resolve.isPending}
							onClick={() => resolve.mutate({ id: item.id })}
						>
							<IconCheck aria-hidden />
							{t("queue.backgroundFailures.resolve")}
						</Button>
					}
				/>
			))}
			<li className="pt-2 text-xs text-muted-foreground">{t("queue.backgroundFailures.help")}</li>
			{resolve.isError ? (
				<li className="pt-2 text-xs text-destructive">{t("queue.backgroundFailures.failed")}</li>
			) : null}
		</QueueCard>
	);
}
